/**
 * Canonical category taxonomy — the SINGLE source of truth for how Talisman
 * categorises entries across the itinerary, expenses, schedule planner, import
 * modals, notifications and the Cloud Function prompts.
 *
 * Two levels:
 *   L1 — the broad MECE grouping. ALWAYS shown with its gold Lucide icon
 *        (section headers, expense totals/chips). Never an emoji.
 *   L2 — the subcategory. Gold line icons (entry cards at 60% opacity).
 *        Emoji remain compatible plain-text/push payloads, not UI glyphs.
 *
 * Anything that needs a category list, an emoji, or an L1↔L2 mapping must import
 * from here rather than redefining its own copy. Backward-compat aliases keep
 * pre-existing Firestore data (old names) rendering correctly.
 */
import { Plane, BedDouble, Utensils, Binoculars, Ticket, ShoppingBag, Tag, Train, BusFront, Ship, Car, CarTaxiFront, Route, ConciergeBell, Bed, House, Backpack, Tent, ChefHat, Coffee, Beer, Martini, IceCreamCone, Landmark, Amphora, Leaf, Map, Theater, FerrisWheel, HandHeart, StickyNote, DollarSign, createLucideIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface SubCat { name: string; emoji: string; icon: LucideIcon }

const ResortIcon = createLucideIcon('Resort', [
  ['path', { d: 'M3 10c2-4 5-6 9-6s7 2 9 6H3Z', key: 'canopy' }],
  ['path', { d: 'm12 4-4 6m4-6 4 6M12 10v8c0 2-1 3-3 3M5 21h14', key: 'stripes' }],
]);
const NightclubIcon = createLucideIcon('Nightclub', [
  ['path', { d: 'M12 1v3', key: 'hanger' }],
  // Leave the upper-right edge open beneath the sparkle, including on tinted surfaces.
  ['path', { d: 'M15 4.6A8 8 0 1 0 19.6 9.4M12 4c-4 3-4 13 0 16m0-16c4 3 4 13 0 16M4.6 9H15M4 12h16M4.6 15h14.8', key: 'ball' }],
  ['path', { d: 'M19 1 20 4.2 23 5.2 20 6.2 19 9.4 18 6.2 15 5.2 18 4.2Z', key: 'glint' }],
]);
export interface L1Cat {
  /** stable key — used for type/sourceType mapping (never shown to users). */
  key: string;
  /** display label. */
  label: string;
  /** gold Lucide icon for this L1. */
  icon: LucideIcon;
  /** appears as an itinerary section? */
  itinerary: boolean;
  /** is an expense category? */
  expense: boolean;
  /** Persisted L1 expense key; changing it would orphan existing records. */
  expenseEmoji: string;
  /** Position in expense pickers and breakdowns, independent of itinerary order. */
  expenseOrder: number;
  /** L2 subcategories (empty for Shopping / Other). */
  subcats: SubCat[];
}

export const TAXONOMY: L1Cat[] = [
  {
    key: 'transport', label: 'Transport', icon: Route, itinerary: true, expense: true, expenseEmoji: '✈️', expenseOrder: 2,
    subcats: [
      { name: 'Flight', emoji: '✈️', icon: Plane }, { name: 'Train', emoji: '🚄', icon: Train }, { name: 'Bus', emoji: '🚌', icon: BusFront },
      { name: 'Boat', emoji: '🚤', icon: Ship }, { name: 'Car', emoji: '🚗', icon: Car }, { name: 'Taxi', emoji: '🚕', icon: CarTaxiFront },
      { name: 'Other', emoji: '🗺️', icon: Route },
    ],
  },
  {
    key: 'accommodation', label: 'Accommodation', icon: BedDouble, itinerary: true, expense: true, expenseEmoji: '🏠', expenseOrder: 1,
    subcats: [
      { name: 'Hotel', emoji: '🏨', icon: ConciergeBell }, { name: 'Resort', emoji: '🏖️', icon: ResortIcon }, { name: 'Motel / B&B', emoji: '🛏️', icon: Bed },
      { name: 'Private rental', emoji: '🔑', icon: House }, { name: 'Hostel', emoji: '🎒', icon: Backpack }, { name: 'Campground', emoji: '⛺', icon: Tent },
      { name: 'Other', emoji: '🏠', icon: BedDouble },
    ],
  },
  {
    key: 'food', label: 'Food & Drink', icon: Utensils, itinerary: true, expense: true, expenseEmoji: '🍽️', expenseOrder: 0,
    subcats: [
      { name: 'Restaurant', emoji: '🍽️', icon: ChefHat }, { name: 'Café', emoji: '☕', icon: Coffee }, { name: 'Pub', emoji: '🍺', icon: Beer },
      { name: 'Bar', emoji: '🍸', icon: Martini }, { name: 'Snacks', emoji: '🍪', icon: IceCreamCone }, { name: 'Other', emoji: '🍴', icon: Utensils },
    ],
  },
  {
    key: 'experiences', label: 'Sightseeing', icon: Binoculars, itinerary: true, expense: true, expenseEmoji: '🎡', expenseOrder: 3,
    subcats: [
      { name: 'Landmark', emoji: '🏛️', icon: Landmark }, { name: 'Museums & Galleries', emoji: '🏺', icon: Amphora },
      { name: 'Nature', emoji: '🌿', icon: Leaf }, { name: 'Tours & Daytrips', emoji: '🗺️', icon: Map }, { name: 'Other', emoji: '📍', icon: Binoculars },
    ],
  },
  {
    key: 'nightlife', label: 'Entertainment & Leisure', icon: Ticket, itinerary: true, expense: true, expenseEmoji: '🎟️', expenseOrder: 4,
    subcats: [
      { name: 'Shows & events', emoji: '🎭', icon: Theater }, { name: 'Amusement', emoji: '🎡', icon: FerrisWheel },
      { name: 'Health & wellness', emoji: '🧘', icon: HandHeart }, { name: 'Nightclub', emoji: '🪩', icon: NightclubIcon }, { name: 'Other', emoji: '🌙', icon: Ticket },
    ],
  },
  { key: 'shopping', label: 'Shopping', icon: ShoppingBag, itinerary: true, expense: true, expenseEmoji: '🛍️', expenseOrder: 5, subcats: [] },
  { key: 'other', label: 'Other', icon: Tag, itinerary: false, expense: true, expenseEmoji: '💲', expenseOrder: 6, subcats: [] },
];

// ── Lookups ───────────────────────────────────────────────────────────────────

export const L1_BY_KEY: Record<string, L1Cat> = Object.fromEntries(TAXONOMY.map(c => [c.key, c]));
export const ITINERARY_L1 = TAXONOMY.filter(c => c.itinerary);
/** Expense choices in their product-specific order, with persisted keys intact. */
export const EXPENSE_L1 = TAXONOMY.filter(c => c.expense).sort((a, b) => a.expenseOrder - b.expenseOrder);

/** L2 list for an L1 key (e.g. subcatsOf('food')). Returns a fresh array. */
export function subcatsOf(key: string): SubCat[] {
  return (L1_BY_KEY[key]?.subcats ?? []).map(s => ({ ...s }));
}

/**
 * Map an itinerary item type / expense sourceType / parser type to its L1 key.
 * Accepts both singular and plural forms and the legacy "attraction(s)" name.
 */
export function l1KeyForType(type?: string | null): string {
  switch ((type || '').toLowerCase()) {
    case 'flight': case 'transport': case 'transports':       return 'transport';
    case 'accommodation': case 'accommodations': case 'stay':  return 'accommodation';
    case 'eat': case 'eats': case 'food': case 'dining':       return 'food';
    case 'attraction': case 'attractions': case 'experience': case 'experiences': return 'experiences';
    case 'nightlife': case 'entertainment':                    return 'nightlife';
    case 'shopping':                                           return 'shopping';
    default:                                                   return 'other';
  }
}

/**
 * The L1 that owns a subcategory name, alias-aware. Subcategory names are
 * unique across the taxonomy apart from "Other", which every L1 has — so an
 * ambiguous name returns null and the caller keeps whatever it already had.
 *
 * This is what lets a picked L2 decide the L1 an item is filed under, rather
 * than the two being set independently and drifting apart: a booking
 * categorised "Live Music" belongs under Entertainment no matter which picker
 * or parser produced it.
 */
export function l1KeyForSubcat(name?: string | null): string | null {
  const norm = normalizeSubcat(name);
  if (!norm || norm === 'Other') return null;
  return TAXONOMY.find(c => c.subcats.some(s => s.name === norm))?.key ?? null;
}

export function l1ForType(type?: string | null): L1Cat {
  return L1_BY_KEY[l1KeyForType(type)];
}

// ── Backward-compat normalisation (old Firestore values → canonical) ───────────

/** Old L2 subcategory names → new canonical names. */
const SUBCAT_ALIAS: Record<string, string> = {
  'B&B': 'Motel / B&B',
  'Club': 'Nightclub',
  'Show': 'Shows & events',
  'Live Music': 'Shows & events',
  'Hotel/Resort': 'Hotel',   // split: historical combined value defaults to Hotel
  'Hotels & Resorts': 'Hotel',
  'Bakery': 'Snacks',
  'Treats': 'Snacks',
  'Bakery & Sweets': 'Snacks',
  'Cafe': 'Café',
};

/** Old L1 labels → new canonical labels. */
const L1_LABEL_ALIAS: Record<string, string> = {
  'Attractions': 'Sightseeing',
  'Experiences': 'Sightseeing',
  'Nightlife & Shows': 'Entertainment & Leisure',
  'Entertainment': 'Entertainment & Leisure',
};

export function normalizeSubcat(name?: string | null): string {
  const n = (name || '').trim();
  return SUBCAT_ALIAS[n] ?? n;
}

export function normalizeL1Label(label?: string | null): string {
  const l = (label || '').trim();
  return L1_LABEL_ALIAS[l] ?? l;
}

// Flat name→emoji map across every subcategory (built after aliasing so a lookup
// of an old name still resolves).
const SUBCAT_EMOJI: Record<string, string> = (() => {
  const m: Record<string, string> = { 'Arts & Culture': '🎨', 'Tour/Experience': '🧗' };
  for (const c of TAXONOMY) for (const s of c.subcats) m[s.name] = s.emoji;
  return m;
})();

/** Emoji for a subcategory name (alias-aware). Falls back to the L1's first
 *  subcat emoji, then a neutral pin. */
export function subcatEmoji(name?: string | null, l1Key?: string): string {
  const norm = normalizeSubcat(name);
  const scoped = l1Key && L1_BY_KEY[l1Key]?.subcats.find(subcat => subcat.name === norm);
  if (scoped) return scoped.emoji;
  if (SUBCAT_EMOJI[norm]) return SUBCAT_EMOJI[norm];
  if (l1Key && L1_BY_KEY[l1Key]?.subcats.length) return L1_BY_KEY[l1Key].subcats[0].emoji;
  return '📍';
}

/** Transport mode → emoji (alias-aware), for transport entries that key off mode. */
export function transportEmoji(mode?: string | null): string {
  return subcatEmoji(mode, 'transport');
}

// ── Transport mode icons ──────────────────────────────────────────────────────

/**
 * Transport modes share their gold icons with saved itinerary legs. Taxi uses
 * the cab-specific icon so it is distinguishable from a self-drive car.
 *
 * Lives here rather than beside any one view so the itinerary, the import
 * wizard and anything later can't drift; tests/taxonomy.test.ts holds the keys
 * to subcatsOf('transport').
 */
export const TRANSPORT_MODE_ICON: Record<string, LucideIcon> = Object.fromEntries(
  L1_BY_KEY.transport.subcats.map(({ name, icon }) => [name, icon]),
);

/** The gold icon for a transport mode, alias-aware, falling back to Route. */
export function transportIcon(mode?: string | null): LucideIcon {
  return TRANSPORT_MODE_ICON[normalizeSubcat(mode)] || Route;
}

const LEGACY_SUBCAT_ICONS: Record<string, LucideIcon> = { 'Arts & Culture': Amphora, 'Tour/Experience': Map };

/** Display only: never re-file legacy items or mutate persisted category keys. */
export function subcategoryIcon(name?: string | null, l1Key?: string): LucideIcon {
  const norm = normalizeSubcat(name);
  if (norm === 'Other') return L1_BY_KEY[l1Key || 'other']?.icon || Tag;
  const scoped = L1_BY_KEY[l1Key || '']?.subcats.find(subcat => subcat.name === norm);
  if (scoped) return scoped.icon;
  for (const category of TAXONOMY) {
    const match = category.subcats.find(subcat => subcat.name === norm);
    if (match) return match.icon;
  }
  return LEGACY_SUBCAT_ICONS[norm] || L1_BY_KEY[l1Key || 'other']?.icon || Tag;
}

/** A parser's coarse attraction type needs an explicit group for ambiguous Other. */
export function attractionL1(data: { category?: string; categoryGroup?: string }): string {
  const group = l1KeyForSubcat(data.category);
  return group === 'nightlife' || group === 'experiences' ? group : (data.categoryGroup === 'Entertainment & Leisure' ? 'nightlife' : 'experiences');
}

export function importedSourceType(type: string, data: { category?: string; categoryGroup?: string }): string {
  if (type === 'attraction') return attractionL1(data) === 'nightlife' ? 'nightlife' : 'attraction';
  return ({ flight: 'transport', eat: 'food', unknown: 'note' } as Record<string, string>)[type] || type;
}

/** Old notification payloads retain emoji for push; display recognised ones as vectors. */
export function notificationItemIcon(emoji: string, l1Key?: string): LucideIcon | null {
  const scoped = L1_BY_KEY[l1Key || '']?.subcats.find(s => s.emoji === emoji);
  if (scoped) return subcategoryIcon(scoped.name, l1Key);
  const fixed: Record<string, LucideIcon> = { '🎟️': Ticket, '🛍️': ShoppingBag, '📝': StickyNote, '🏷️': Tag, '💲': DollarSign };
  if (fixed[emoji]) return fixed[emoji];
  const legacy: Record<string, string> = { '🎶': 'Shows & events', '🎨': 'Arts & Culture', '🧗': 'Tour/Experience', '🍪': 'Snacks', '🍦': 'Snacks' };
  if (legacy[emoji]) return subcategoryIcon(legacy[emoji]);
  for (const category of TAXONOMY) {
    const subcat = category.subcats.find(s => s.emoji === emoji);
    if (subcat) return subcategoryIcon(subcat.name, category.key);
  }
  return null;
}

// ── Keyword auto-suggest ──────────────────────────────────────────────────────

/**
 * What the traveller types → the L1 it probably belongs to. Lives here rather
 * than beside any one form because every category picker in the app should
 * guess the same way; import `suggestL1Key` instead of keeping a private list.
 *
 * These are the words people actually write in a one-line description, so the
 * lists lean towards specific things ("gelato", "pad thai", "funicular") rather
 * than the category names themselves — a traveller logging a coffee writes
 * "flat white", not "food & drink". Plurals and the common British/American
 * and hyphenation variants are handled by the compiler below, so each entry is
 * the bare singular; only genuinely different spellings need their own entry.
 *
 * Nothing maps to 'other': it is reachable by an explicit pick, never guessed.
 */
const L1_KEYWORDS: Record<string, string[]> = {
  food: [
    // meals & venues
    'breakfast', 'brunch', 'lunch', 'dinner', 'supper', 'meal', 'food', 'snack', 'nibble',
    'restaurant', 'resto', 'eatery', 'diner', 'bistro', 'brasserie', 'trattoria', 'osteria',
    'tavern', 'taverna', 'gastropub', 'izakaya', 'canteen', 'cafeteria', 'buffet', 'food court', 'food hall',
    'street food', 'takeaway', 'takeout', 'picnic', 'room service', 'deli', 'delicatessen',
    // cafés, bakeries, sweet things
    'cafe', 'café', 'coffee', 'espresso', 'latte', 'cappuccino', 'flat white', 'americano',
    'macchiato', 'mocha', 'cortado', 'affogato', 'tea', 'chai', 'matcha', 'boba', 'bubble tea',
    'juice', 'smoothie', 'milkshake', 'bakery', 'boulangerie', 'patisserie', 'pastry',
    'croissant', 'baguette', 'bagel', 'donut', 'doughnut', 'muffin', 'scone', 'cake', 'cupcake',
    'brownie', 'cookie', 'biscuit', 'waffle', 'pancake', 'crepe', 'crêpe', 'churro', 'macaron',
    'tart', 'dessert', 'ice cream', 'gelato', 'sorbet', 'sundae', 'froyo', 'frozen yoghurt',
    'yoghurt', 'yogurt', 'chocolate', 'candy', 'sweet', 'pudding', 'cannoli', 'tiramisu',
    // drinks
    'drink', 'beverage', 'bar', 'pub', 'beer', 'ale', 'lager', 'pint', 'wine', 'vino',
    'prosecco', 'champagne', 'cocktail', 'mojito', 'margarita', 'negroni', 'spritz', 'aperol',
    'aperitif', 'sangria', 'whisky', 'whiskey', 'bourbon', 'gin', 'vodka', 'rum', 'tequila',
    'sake', 'soju', 'cider', 'happy hour', 'brewery', 'taproom', 'winery', 'vineyard',
    'distillery', 'cellar door',
    // dishes
    'pizza', 'pasta', 'gnocchi', 'risotto', 'lasagne', 'lasagna', 'burger', 'cheeseburger',
    'hot dog', 'sandwich', 'toastie', 'wrap', 'taco', 'burrito', 'quesadilla', 'nacho',
    'empanada', 'arepa', 'ceviche', 'sushi', 'sashimi', 'nigiri', 'tempura', 'katsu', 'udon',
    'ramen', 'noodle', 'pho', 'pad thai', 'dumpling', 'dim sum', 'bao', 'bibimbap', 'kimchi',
    'curry', 'tapas', 'paella', 'pierogi', 'kebab', 'shawarma', 'falafel', 'hummus', 'gyro',
    'souvlaki', 'schnitzel', 'steak', 'bbq', 'barbecue', 'grill', 'roast', 'seafood', 'oyster',
    'lobster', 'crab', 'prawn', 'shrimp', 'fish and chips', 'fish & chips', 'salad', 'soup',
    'poke', 'brekkie',
    // provisions
    'grocery', 'groceries', 'supermarket', 'minimart', 'convenience store', 'butcher',
    'greengrocer', 'bottle shop', 'liquor store', 'off licence', 'off license',
  ],
  accommodation: [
    'hotel', 'motel', 'hostel', 'inn', 'guesthouse', 'guest house', 'bnb', 'b&b',
    'bed and breakfast', 'airbnb', 'vrbo', 'resort', 'lodge', 'lodging', 'cabin', 'chalet',
    'villa', 'apartment', 'homestay', 'ryokan', 'riad', 'pension', 'campsite', 'campground',
    'camping', 'caravan', 'holiday park', 'glamping', 'dorm', 'bunk', 'accommodation',
    // one 'm' is the misspelling people reliably make, so it has to match too
    'accomodation', 'check in', 'check-in', 'checkout', 'check-out', 'suite', 'nights stay',
    'city tax', 'tourist tax', 'resort fee', 'cleaning fee',
  ],
  transport: [
    'taxi', 'cab', 'uber', 'lyft', 'grab', 'bolt', 'didi', 'ola', 'rideshare', 'ride share',
    'train', 'rail', 'railway', 'metro', 'subway', 'underground', 'tube', 'tram', 'streetcar',
    'light rail', 'bus', 'coach', 'shuttle', 'minibus', 'flight', 'airfare', 'airline',
    'airport', 'plane', 'aeroplane', 'airplane', 'baggage', 'checked bag', 'seat selection',
    'ferry', 'boat', 'catamaran', 'water taxi', 'car hire', 'hire car', 'car rental',
    'rental car', 'scooter', 'moped', 'motorbike', 'bike', 'bicycle', 'ebike', 'e-bike',
    'fuel', 'petrol', 'diesel', 'gas', 'charging', 'parking', 'toll', 'congestion charge',
    'vignette', 'transfer', 'funicular', 'cable car', 'chairlift', 'rickshaw', 'tuk tuk',
    'tuktuk', 'oyster card', 'travel card', 'transit', 'public transport', 'mileage',
  ],
  experiences: [
    'museum', 'gallery', 'exhibition', 'exhibit', 'landmark', 'monument', 'memorial', 'statue',
    'cathedral', 'church', 'basilica', 'chapel', 'mosque', 'synagogue', 'temple', 'shrine',
    'pagoda', 'castle', 'palace', 'fort', 'fortress', 'ruin', 'archaeological', 'historic site',
    'heritage', 'old town', 'tour', 'guided tour', 'walking tour', 'sightseeing', 'sight',
    'excursion', 'day trip', 'safari', 'hike', 'hiking', 'trek', 'trekking', 'trail', 'summit',
    'national park', 'park', 'garden', 'botanical', 'zoo', 'aquarium', 'wildlife', 'sanctuary',
    'waterfall', 'beach', 'lake', 'island', 'viewpoint', 'lookout', 'observation deck', 'tower',
    'bridge', 'canyon', 'volcano', 'glacier', 'hot spring', 'onsen', 'cave', 'cenote',
    'snorkel', 'snorkelling', 'snorkeling', 'dive', 'diving', 'scuba', 'kayak', 'kayaking',
    'paddleboard', 'surf', 'surfing', 'ski', 'skiing', 'snowboard', 'rafting', 'zipline',
    'ziplining', 'bungee', 'paragliding', 'skydive', 'skydiving', 'hot air balloon', 'admission',
    'entry fee', 'entrance fee', 'cruise',
  ],
  nightlife: [
    'spa', 'massage', 'hammam', 'thermal bath', 'wellness', 'yoga', 'sauna', 'cooking class', 'workshop', 'parasailing',
    'concert', 'gig', 'show', 'musical', 'theatre', 'theater', 'opera', 'ballet', 'cinema',
    'movie', 'film', 'imax', 'ticket', 'festival', 'carnival', 'parade', 'club', 'nightclub',
    'disco', 'rave', 'live music', 'karaoke', 'comedy', 'stand up', 'standup', 'bowling',
    'arcade', 'escape room', 'casino', 'bingo', 'billiards', 'quiz night', 'stadium', 'arena',
    'grand prix', 'football', 'soccer', 'basketball', 'baseball', 'cricket', 'rugby', 'tennis',
    'amusement park', 'theme park', 'rollercoaster', 'waterpark', 'fairground', 'circus',
  ],
  shopping: [
    'shopping', 'shop', 'store', 'boutique', 'souvenir', 'gift', 'market', 'bazaar', 'mall',
    'shopping centre', 'shopping center', 'outlet', 'department store', 'clothes', 'clothing',
    'apparel', 'shoe', 'sneaker', 'jewellery', 'jewelry', 'handbag', 'cosmetics', 'makeup',
    'perfume', 'pharmacy', 'chemist', 'toiletries', 'sunscreen', 'book', 'bookshop',
    'bookstore', 'electronics', 'sim card', 'esim', 'adapter', 'postcard', 'magnet',
    'duty free',
  ],
};

/**
 * The order guesses are tried in. First match wins, so a description that could
 * belong to two categories ("museum cafe") resolves the same way every time
 * rather than by whichever word happened to come first in the string.
 *
 * Food leads because food words are the most specific; the pairs that actually
 * collide are deliberate — "supermarket" is food, not shopping; "parking" is
 * transport, not a park; "flight ticket" is transport, not entertainment.
 */
const SUGGEST_ORDER = ['food', 'accommodation', 'transport', 'experiences', 'nightlife', 'shopping'];

const L1_PATTERNS: { key: string; re: RegExp }[] = SUGGEST_ORDER.map(key => {
  const alternation = L1_KEYWORDS[key]
    // Longest first so "ice cream" is tried before "cream" would be, and an
    // entry never loses to a shorter one that prefixes it.
    .slice().sort((a, b) => b.length - a.length)
    .map(word => word
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      // A typed phrase may be spaced, hyphenated or run together: "ice cream",
      // "ice-cream", "icecream".
      .replace(/ /g, '[\\s-]?')
      // …and almost anything here can be pluralised. `e?s` covers both
      // "pizzas" and "sandwiches"/"buses".
      + '(?:e?s)?')
    .join('|');
  return { key, re: new RegExp(`\\b(?:${alternation})\\b`, 'i') };
});

/** The L1 key suggested by keywords in `text`, or null if nothing matches (in
 *  which case the current selection is left alone — never overridden). */
export function suggestL1Key(text?: string | null): string | null {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;
  // Specific leisure phrases outrank generic words such as park and food.
  if (/\b(?:cooking[\s-]+class|theme[\s-]+park|amusement[\s-]+park|spa|massage|wellness|parasailing)\b/i.test(trimmed)
    && !/\b(?:hotel|resort)\b/i.test(trimmed)) return 'nightlife';
  return L1_PATTERNS.find(p => p.re.test(trimmed))?.key ?? null;
}
